import { useEffect, useCallback } from "react"
import { m, AnimatePresence } from "framer-motion"

import { Stack } from "@mui/system"
import { Box, Card, Typography, LinearProgress, CircularProgress } from "@mui/material"

import { useTranslate } from "src/locales"
import { useAppDispatch, useAppSelector } from "src/store/store"
import { updateBackgroundTaskState } from "src/store/slices/app/appSlice"
import { useGetBackgroundTasksQuery } from "src/services/system/system.service"
import { BackgroundTaskType, BackgroundTaskStatus } from "src/services/system/system.types"

const ACTIVE_STATUSES = [BackgroundTaskStatus.PENDING, BackgroundTaskStatus.IN_PROGRESS];

export default function BackgroundTaskManager() {

  const { t } = useTranslate()

  const backgroundState = useAppSelector(state => state.app.background_task_state);
  const dispatch = useAppDispatch();

  const { data: backgroundTasks } = useGetBackgroundTasksQuery({
    status: [BackgroundTaskStatus.PENDING, BackgroundTaskStatus.IN_PROGRESS]
  }, {
    pollingInterval: backgroundState.pending_count > 0 ? 3000 : undefined
  });

  useEffect(() => {
    if (backgroundTasks) {
      const inProgressTask = backgroundTasks.find(task => [BackgroundTaskStatus.IN_PROGRESS].includes(task.status));
      const pendingTasks = backgroundTasks.filter(task => [BackgroundTaskStatus.PENDING, BackgroundTaskStatus.IN_PROGRESS].includes(task.status));
      const completedTasks = backgroundTasks.filter(task => task.status === BackgroundTaskStatus.COMPLETED);
      const failedTasks = backgroundTasks.filter(task => task.status === BackgroundTaskStatus.FAILED);

      dispatch(updateBackgroundTaskState({
        task_type: inProgressTask?.type || '' as BackgroundTaskType,
        total_count: backgroundTasks.length,
        pending_count: pendingTasks.length,
        completed_count: completedTasks.length,
        failed_count: failedTasks.length
      }))
    }
  }, [backgroundTasks, dispatch]);

  // A CSV import reports how many rows it has done, so the banner shows that, on every page, for as
  // long as the import runs, including after its dialog is closed.
  const csvImport = backgroundTasks?.find(
    (task) => task.type === BackgroundTaskType.IMPORT_CSV_RECORDS && ACTIVE_STATUSES.includes(task.status)
  );
  const csvImportProgress = csvImport?.progress;

  const getTaskText = useCallback(() => {

    if (!backgroundState.pending_count) return null;

    if (csvImport?.import_entity) {
      return t(`system.background_tasks.info_labels.import_csv_records.${csvImport.import_entity}`, {
        processed: (csvImportProgress?.processed ?? 0).toLocaleString(),
        total: (csvImportProgress?.total ?? 0).toLocaleString(),
      });
    }

    switch (backgroundState.task_type) {
      case BackgroundTaskType.FINALIZE_APPLY_INTENT:
        return t('system.background_tasks.info_labels.finalize_apply_intent', { completed: backgroundState.completed_count, total: backgroundState.total_count });
      case BackgroundTaskType.FINALIZE_CONNECT_INTENT:
        return t('system.background_tasks.info_labels.finalize_connect_intent', { completed: backgroundState.completed_count, total: backgroundState.total_count });
      case BackgroundTaskType.IMPORT_CANDIDATE_PROFILE:
        return t('system.background_tasks.info_labels.import_candidate_profile');
      case BackgroundTaskType.IMPORT_APPLICATION_PROFILE:
        return t('system.background_tasks.info_labels.import_application_profile');
      default:
        return null;
    }

  }, [t, backgroundState, csvImport?.import_entity, csvImportProgress]);

  if (!backgroundState.pending_count) return null;

  return (
    <AnimatePresence>
      <m.div
        initial={{ y: -100, opacity: 0, translateY: '50%' }}
        animate={{ y: 12, opacity: 1, translateY: 0 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        style={{
          position: 'fixed',
          top: 5,
          left: '50%',
          width: '50%',
          transform: 'translate(-50%, 0)',
          marginLeft: '-25%',
          zIndex: 1200,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            boxShadow: (theme) => theme.customShadows.z16,
            borderRadius: 2,
            p: 2,
            bgcolor: (theme) => theme.palette.info.darker,
            maxWidth: 320,
            width: 'auto',
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center' justifyContent="space-between" sx={{ overflow: 'hidden' }}>
            <Box>
              <Typography variant='subtitle2' color="white" textAlign="center">
                {getTaskText()}
              </Typography>
              {csvImportProgress && csvImportProgress.total > 0 && (
                <LinearProgress
                  variant="determinate"
                  color="inherit"
                  value={Math.round((csvImportProgress.processed / csvImportProgress.total) * 100)}
                  sx={{ mt: 1, color: 'common.white' }}
                />
              )}
            </Box>
            <Box>
              <CircularProgress size={24} color='info' />
            </Box>
          </Stack>
        </Card>
      </m.div>
    </AnimatePresence>
  )
}